import type { TaskPerson } from '@obras/shared';
import { X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { EASE_OUT_QUINT } from '../../components/motion.js';
import { Avatar } from '../../components/ui/Feedback.js';
import { SingleSelect } from '../../components/ui/MultiSelect.js';
import type { OrgMember } from '../usuarios/api.js';

/**
 * A equipe da tarefa (design.md §8, Tarefas): quem participa dela, cada pessoa
 * numa linha, com o avatar, e o seletor com busca para incluir mais alguém.
 *
 * Quem criou vem primeiro, marcado, e não sai — é dele a exclusão. Os outros
 * saem pelo × da ponta, e quem tira a si mesmo deixa de ver a tarefa (o modal
 * pergunta antes).
 */
export function Equipe({
  membros,
  criadorId,
  euId,
  pessoasDoOrgao,
  onIncluir,
  onTirar,
  limite,
}: {
  membros: readonly TaskPerson[];
  criadorId: string;
  euId: string;
  pessoasDoOrgao: readonly OrgMember[];
  onIncluir: (userId: string) => void;
  onTirar: (userId: string) => void;
  limite: number;
}) {
  const presentes = new Set(membros.map((membro) => membro.id));
  const opcoes = pessoasDoOrgao
    .filter((pessoa) => !presentes.has(pessoa.id))
    .map((pessoa) => ({
      value: pessoa.id,
      label: pessoa.name,
      detail: pessoa.jobTitle ?? undefined,
    }));

  return (
    <section>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <h3 className="text-[13px] font-medium">Equipe</h3>
        <span className="tabular text-[12px] text-ink-3">{membros.length}</span>
      </div>

      <ul className="flex flex-col">
        <AnimatePresence initial={false}>
          {membros.map((membro) => {
            const criador = membro.id === criadorId;
            return (
              <motion.li
                key={membro.id}
                layout="position"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2, ease: EASE_OUT_QUINT }}
                className="overflow-hidden"
              >
                <div className="group flex h-10 items-center gap-2.5">
                  <Avatar name={membro.name} url={membro.avatarUrl} size={24} />
                  <span className="min-w-0 flex-1 truncate text-sm">
                    {membro.name}
                    {membro.id === euId ? <span className="text-ink-3"> (você)</span> : null}
                  </span>
                  {criador ? (
                    <span className="shrink-0 text-[12px] text-ink-3">criou</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onTirar(membro.id)}
                      aria-label={
                        membro.id === euId ? 'Sair da tarefa' : `Tirar ${membro.name} da tarefa`
                      }
                      title={membro.id === euId ? 'Sair da tarefa' : 'Tirar da tarefa'}
                      className="grid size-7 shrink-0 place-items-center text-ink-3 opacity-0 transition-[opacity,color] group-hover:opacity-100 group-focus-within:opacity-100 hover:text-danger focus-visible:opacity-100 max-sm:opacity-100"
                    >
                      <X size={14} strokeWidth={2} aria-hidden />
                    </button>
                  )}
                </div>
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ul>

      {membros.length < limite ? (
        <SingleSelect
          label="Incluir pessoa na tarefa"
          placeholder={opcoes.length ? 'Incluir pessoa' : 'Todo o órgão já está aqui'}
          options={opcoes}
          value={null}
          onChange={onIncluir}
          searchable
          className="mt-1.5"
        />
      ) : (
        <p className="mt-1.5 text-[12px] text-ink-3">Limite de {limite} pessoas por tarefa.</p>
      )}
    </section>
  );
}
